<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useStore} from "vuex";
import axios from "axios";
import RegistrationError from "../../../../../views/login/registration/components/RegistrationError.vue";
import PaymentElement from "@/components/billing/partials/PaymentElement.vue";
import LoadingSpinner from "@/components/ui/LoadingSpinner.vue";
import {usePaymentElement} from "@/composables/usePaymentElement";
import {useCelloAttribution} from "@/composables/useCelloAttribution";

defineProps({
  availableBillingPeriods: {
    type: Object,
    required: true,
  },
});

const defaultSubmitButtonMessage = `Get Started - Risk Free<br><small>Love it or get a full refund</small>`;

const submitButtonDisabled = ref(true);
const submitButtonDisabledPrice = ref(true);
const buttonMessage = ref(defaultSubmitButtonMessage);
const stripeCardErrorMessage = ref();
const store = useStore();
const registrationComplete = ref(false);
const formSubmitting = ref(false);

const configuration = computed(() => store.getters['registration/configuration']);
const stripeRedirect = computed(() => store.getters['registration/stripeRedirect']);

const paymentElementRef = ref(null);
const paymentElementLoading = ref(true);

const price = computed(() => store.getters['registration/price']);
const finalPrice = computed(() => price.value.discount ? price.value.discount : price.value.fullPrice);
const discountedFromPrice = computed(() => {
  let priceWithoutDiscount = price.value.discount ? price.value.fullPrice : null;

  if (priceWithoutDiscount !== null) {
    return price.value.currency === 'GBP' ? priceWithoutDiscount.incVat : priceWithoutDiscount.exVat;
  }

  return price.value.monthlyCostComparison;
});

const {
  clientSecret,
  confirmPayment,
  initializeStripe,
  stripe
} = usePaymentElement();
const { getCelloUcc } = useCelloAttribution();

const displayDiscountedFromPrice = computed(() => {
  return new Intl.NumberFormat(
    'en-EN',
    { style: 'currency', currency: configuration.value.currency }
  ).format(discountedFromPrice.value / 100);
});

onMounted(async () => {
  await store.dispatch('registration/getPackagePrice');

  submitButtonDisabledPrice.value = false;

  await initializeStripe();

  const params = new URLSearchParams(window.location.search);
  const redirectStatus = params.get('redirect_status');

  if (redirectStatus === 'failed') {
    stripeCardErrorMessage.value = 'Sorry, there was a problem taking payment. You have not been charged.';
  }

  if (stripeRedirect.value) {
    return;
  }

  try {
    const { data } = await createStripeCustomerAndIncompleteSubscription();
    const { registrationToken } = data;

    await store.dispatch('registration/setRegistrationToken', {
      registrationToken
    });
  } catch (error) {
    console.error('Error initializing payment:', error);
  }
});

watch(() => configuration.value.billingPeriod, async () => {
  await store.dispatch('registration/getPackagePrice');
});

const submitStripeForm = async () => {
  formSubmitting.value = true;
  stripeCardErrorMessage.value = null;
  submitButtonDisabled.value = true;
  buttonMessage.value = `Submitting payment...<br><small>This will only take a few moments!</small>`;

  try {
    if (clientSecret.value) {
      const result = await confirmPayment(store.getters['registration/registrationToken'].registrationToken);

      if (result.error) {
        const { message } = result.error;
        submitButtonDisabled.value = false;
        stripeCardErrorMessage.value = message;
        buttonMessage.value = defaultSubmitButtonMessage;
      }
    } else {
      throw new Error('Payment element not available or client secret missing');
    }
  } catch (error) {
    submitButtonDisabled.value = false;
    stripeCardErrorMessage.value = 'Sorry, there was a problem taking payment. You have not been charged.';
    buttonMessage.value = defaultSubmitButtonMessage;
  }
}

const removeCustomer = () => {
  const { registrationToken } = store.getters['registration/registrationToken'];

  return axios.post('/ajax/remove-customer', {registrationToken});
}

const createStripeCustomerAndIncompleteSubscription = async () => {
  const { billingPeriod, currency, packageName, discount } = configuration.value;
  const celloUcc = await getCelloUcc();

  const response = await axios.post('/ajax/v2/billing/packages/customers/store', {
    name: store.getters['registration/name'],
    email: store.getters['registration/email'],
    password: store.getters['registration/password'],
    operationCountry: store.getters['registration/operationCountry'],
    currency: currency,
    billingPeriod: billingPeriod,
    package: packageName,
    discount: discount,
    toltReferral: window.tolt_referral,
    celloUcc,
  });

  if (response.data.clientSecret) {
    clientSecret.value = response.data.clientSecret;
  }

  return response;
}

const handlePaymentElementReady = () => {
  paymentElementLoading.value = false;
  submitButtonDisabled.value = false;
}

const handlePageExit = () => {
  if (formSubmitting.value) {
    return;
  }

  if (!registrationComplete.value) {
    removeCustomer();
  }
};

window.addEventListener('beforeunload', handlePageExit);

window.addEventListener('unload', handlePageExit);

onUnmounted(() => {
  window.removeEventListener('beforeunload', handlePageExit);
  window.removeEventListener('unload', handlePageExit);
});

const isAnnual = computed(() => configuration.value.billingPeriod === 'YEAR');
</script>

<template>
  <div
    v-if="price"
    class="stripe-form"
  >
    <RegistrationError v-if="stripeCardErrorMessage">
      <span v-html="stripeCardErrorMessage" />
    </RegistrationError>

    <div class="price-header">
      <div
        v-if="!submitButtonDisabledPrice"
        class="price-container"
      >
        <div class="price">
          <span
            class="price-hero"
            v-html="finalPrice.exVatDisplay"
          />
          <div
            v-if="discountedFromPrice !== null"
            class="price-details"
          >
            <span
              class="price-saving"
              v-html="displayDiscountedFromPrice"
            />
          </div>
        </div>
        <div
          v-if="price.showTax"
          class="price-tax"
          v-html="finalPrice.incVatDisplay"
        />
      </div>
      <div v-else>
        Loading...
      </div>
    </div>

    <div class="update-text">
      <span v-if="isAnnual && !configuration.discount">Save 2 months with a yearly subscription.</span> Cancel <span v-if="!isAnnual">or upgrade to annual</span> at any time.
    </div>

    <LoadingSpinner
      v-if="paymentElementLoading"
      class="tw-text-[#3BB390]"
    >
      Loading...
    </LoadingSpinner>

    <PaymentElement
      v-show="!paymentElementLoading"
      v-if="stripe && clientSecret"
      ref="paymentElementRef"
      :show-notice="false"
      @ready="handlePaymentElementReady"
      @error="(error) => stripeCardErrorMessage = error.message"
    />

    <div class="form-input-group">
      <button
        :disabled="submitButtonDisabled"
        class="btn-register"
        type="button"
        @click="submitStripeForm"
      >
        <span v-html="buttonMessage" />
      </button>
    </div>

    <div class="small-text">
      You will be charged today, but if Hike isn’t for you please email support@hikeseo.co within the first 14 days for
      a full refund.
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stripe-form {
  font-family: 'DM Sans', sans-serif;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  background: #FFFFFF;
  border: 1px solid #D8D8D8;
  box-sizing: border-box;
  border-radius: 4px;
  gap: 1.5rem;
}

.btn-register {
  background-color: #4CE0B3;
  color: #0B132B;
  text-align: center;
  border: none;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s ease;
  font-size: inherit;
  font-family: 'DM Sans', sans-serif;
  padding: 0.5rem;
  border-radius: 4px;

  small {
    font-size: x-small;
  }

  &:hover {
    background-color: #3BB390;
  }

  &:disabled {
    opacity: 0.7;
  }
}

.price-header {
  display: flex;
  width: 100%;
  justify-content: space-between;

  .price-container {
    display: flex;
    flex-direction: column;
    width: 100%;

    .price {
      display: flex;
      width: 100%;

      .price-hero {
        font-family: 'DM Sans', sans-serif;
        font-weight: 700;
        color: #3E3E44;
        overflow: hidden;
        line-height: 1.1;
        font-size: 3rem;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
      }

      .price-details {
        display: flex;
        flex-direction: column;
        padding-top: 0.4rem;

        .no-discount {
          padding-top: 1.25rem;
        }
      }

      .price-saving {
        font-weight: 700;
        text-decoration: line-through;
        padding-left: 0.7rem;
      }
    }
  }

  .update-text {
    color: #3E3E44;
    font-family: DM Sans, sans-serif;
    font-style: normal;
    font-weight: bold;
    font-size: 12px;
    line-height: 16px;
    text-transform: uppercase;
    text-align: center;
  }

  .small-text {
    color: #3E3E44;
    font-family: DM Sans, sans-serif;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .form-input-group > * {
    width: 100%;
    padding: 1rem;
    background: #FFFFFF;
    border: 1px solid #D8D8D8;
    box-sizing: border-box;
    border-radius: 4px;
  }
}

.price-switch-container {
  width: 40%;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.8rem;

  .switch {
    position: relative;
    display: inline-block;
    width: 45px;
    min-width: 45px;
    height: 18px;

    input {
      opacity: 0;
      width: 0;
      height: 0;
    }
  }

  input:checked + .slider {
    background-color: #4CE0B3;
  }

  input:focus + .slider {
    box-shadow: 0 0 1px #4CE0B3;
  }

  input:checked + .slider:before {
    transform: translateX(23px);
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #4CE0B3;
    -webkit-transition: .4s;
    transition: .4s;

    &.round {
      border-radius: 34px;

      &::before {
        position: relative;
        display: block;
        background-color: #fff;
        bottom: 4px;
        content: "";
        height: 14px;
        width: 14px;
        top: 2px;
        left: 4px;
        border-radius: 50%;
        transition: transform 0.2s ease;
      }
    }
  }

  &:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    -webkit-transition: .4s;
    transition: .4s;
  }
}

@media only screen and (max-width: 599px) {
  .price-header {
    flex-direction: column;
    align-items: center;

    .price-container {
      width: auto;
    }

    .price-switch-container {
      padding-top: .5rem;
    }
  }
}

.card-container {
  border: 2px solid #e3e2e2;
  padding: 18px 10px;
}

</style>
